import {
  ChangeDetectionStrategy,
  ChangeDetectorRef,
  Component,
  EventEmitter,
  Input,
  OnDestroy,
  OnInit,
  Output
} from '@angular/core';
import { Subject } from 'rxjs';
import { takeUntil } from 'rxjs/operators';
import { Store } from '@ngrx/store';

import { OptionsRange, RangeFilter, RangeFilters } from '~shared/models';
import * as fromFilters from '~app/modules/filters/store';
import { Attribute } from '~app/modules/filters/models';

export enum MinOrMaxLabel {
  min = 'min',
  max = 'max'
}

@Component({
  selector: 'app-search-range-filter',
  templateUrl: './range-filter.component.html',
  styleUrls: ['./range-filter.component.scss'],
  changeDetection: ChangeDetectionStrategy.OnPush
})
export class RangeFilterComponent implements OnInit, OnDestroy {

  @Input() filterTypes: string;
  @Input() attributeName: string;
  @Output() rangeFilterChanged = new EventEmitter<RangeFilters>();

  min: number;
  max: number;
  from: number;
  to: number;

  lastAppliedRange: RangeFilter = null;

  private unsubscribe$ = new Subject();

  constructor(private store: Store<fromFilters.State>,
              private cd: ChangeDetectorRef) {}

  ngOnInit(): void {
    this.store.select(fromFilters.getOptionRangesFilters)
      .pipe(takeUntil(this.unsubscribe$))
      .subscribe((priceRange: RangeFilters) => {
        this.onRangeStoreChange(priceRange[this.filterTypes]);
      });

    this.store.select(fromFilters.getOptionsRange)
      .pipe(takeUntil(this.unsubscribe$))
      .subscribe(((attributes: Attribute[]) => {
        this.onMinMaxStoreChange(attributes.find(element => element.id === this.filterTypes)?.optionsRange);
      }));
  }

  ngOnDestroy(): void {
    this.unsubscribe$.next();
    this.unsubscribe$.complete();
  }

  onFromChange(event: Event): void {
    let from = (event.target as any).valueAsNumber;
    from = Math.min(this.to - 1, Math.max(from, this.min));
    this.from = isNaN(from) ? this.min : from;
    this.fireRangeFilterChangeEvent(this.filterTypes, MinOrMaxLabel.min);
  }

  onToChange(event: Event) {
    let to = (event.target as any).valueAsNumber;
    to = Math.min(this.max, Math.max(to, this.from + 1));
    this.to = isNaN(to) ? this.max : to;
    this.fireRangeFilterChangeEvent(this.filterTypes, MinOrMaxLabel.max);
  }

  private onRangeStoreChange(priceRange: RangeFilter): void {
    if (!priceRange || priceRange.from == null || priceRange.to == null) {
      this.from = this.min;
      this.to = this.max;
      this.lastAppliedRange = null;
    } else {
      this.lastAppliedRange = {...priceRange};
      this.from = priceRange.from;
      this.to = priceRange.to;
    }
    this.cd.markForCheck();
  }

  private onMinMaxStoreChange(minMaxRange: OptionsRange): void {
    if (!minMaxRange || minMaxRange.max == null || minMaxRange.min == null) {
      return;
    }

    if (minMaxRange.min === this.min && minMaxRange.max === this.max) {
      return;
    }

    this.min = minMaxRange.min;
    this.max = minMaxRange.max;

    if (this.lastAppliedRange) {
      this.from = Math.max(this.lastAppliedRange.from, this.min);
      this.to = Math.min(this.lastAppliedRange.to, this.max);
    } else {
      this.from = this.min;
      this.to = this.max;
    }
    this.cd.markForCheck();
  }

  private fireRangeFilterChangeEvent(filterType, minOrMaxLabel: MinOrMaxLabel): void {
    const data: RangeFilters = {};
    data[filterType] = {type: filterType, from: this.from, to: this.to, minOrMaxLabel};
    this.rangeFilterChanged.emit(data);
  }
}
